import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLang, localeFor } from '../LangContext';

const Reveal = ({ children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay }}>
    {children}
  </motion.div>
);

const CONTENT = {
  'terms': {
    tag: 'Legal',
    title: 'Terms & Conditions',
    intro: 'These Terms & Conditions govern your use of the All Things platform. By accessing or using our services, you agree to be bound by these terms.',
    sections: [
      { heading: '1. Introduction', body: 'Welcome to All Things. These Terms & Conditions outline the rules and regulations for the use of our marketplace, marketing services and related features.' },
      { heading: '2. Eligibility', body: 'You must be at least 18 years old and capable of entering into a legally binding agreement to use our services. By registering, you confirm that all information you provide is accurate and complete.' },
      { heading: '3. Accounts', body: 'You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. Notify us immediately of any unauthorised use.' },
      { heading: '4. Services & Fees', body: 'Our services are provided as described on the platform. Any applicable fees are clearly displayed before you confirm a transaction or booking.' },
      { heading: '5. User Conduct', body: 'You agree not to misuse the platform, upload unlawful content, impersonate others, or engage in any activity that could harm other users or our services.' },
      { heading: '6. Intellectual Property', body: 'All content, design, logos and materials on this platform are the property of All Things unless otherwise stated and may not be reproduced without permission.' },
      { heading: '7. Termination', body: 'We reserve the right to suspend or terminate accounts that violate these terms or that pose a risk to the platform or other users.' },
      { heading: '8. Limitation of Liability', body: 'To the maximum extent permitted by law, All Things shall not be liable for any indirect, incidental or consequential damages arising from your use of the platform.' },
      { heading: '9. Governing Law', body: 'These terms are governed by the laws of the Federal Democratic Republic of Ethiopia. Any disputes shall be subject to the exclusive jurisdiction of its courts.' },
    ],
  },
  'privacy-policy': {
    tag: 'Legal',
    title: 'Privacy Policy',
    intro: 'Your privacy matters to us. This policy explains what information we collect, how we use it, and the choices you have.',
    sections: [
      { heading: '1. Information We Collect', body: 'We collect information you provide directly, such as your name, email address, phone number and payment details, as well as usage data collected automatically.' },
      { heading: '2. How We Use Your Information', body: 'Your information is used to operate and improve our services, process transactions, send updates, provide support and personalise your experience.' },
      { heading: '3. Cookies', body: 'Our platform uses cookies and similar technologies to remember your preferences and understand how you interact with our site.' },
      { heading: '4. Sharing of Information', body: 'We do not sell your personal information. We only share it with trusted partners who help us operate the platform or where required by law.' },
      { heading: '5. Security', body: 'We apply reasonable technical and organisational measures to protect your information from unauthorised access, alteration or disclosure.' },
      { heading: '6. Your Rights', body: 'You may request access to, correction of, or deletion of your personal information at any time by contacting our support team.' },
      { heading: '7. Contact', body: 'If you have questions about this Privacy Policy, please contact us at allthingsethiopia2026@gmail.com.' },
    ],
  },
  'billing-policy': {
    tag: 'Legal',
    title: 'Billing Policy',
    intro: 'This Billing Policy explains how payments, fees and refunds work on the All Things platform.',
    sections: [
      { heading: '1. Payment Methods', body: 'We accept the payment methods displayed at checkout, including supported bank transfers and mobile payment options.' },
      { heading: '2. Pricing', body: 'All prices are displayed in the applicable currency before you confirm your purchase. Prices may change at any time without prior notice.' },
      { heading: '3. Billing Cycle', body: 'Charges are processed at the time of purchase unless otherwise stated. Recurring services will be billed at the beginning of each billing period.' },
      { heading: '4. Failed Payments', body: 'If a payment fails, the transaction will not be completed and you will be notified. Please verify your payment details and try again.' },
      { heading: '5. Refunds', body: 'Refunds are handled according to the seller\u2019s return policy. If you believe a charge was made in error, contact our support team within 14 days.' },
    ],
  },
  'cookie-policy': {
    tag: 'Legal',
    title: 'Cookie Policy',
    intro: 'This policy explains what cookies are and how we use them on the All Things platform.',
    sections: [
      { heading: '1. What Are Cookies', body: 'Cookies are small text files stored on your device when you visit a website. They help the site work properly and remember your preferences.' },
      { heading: '2. How We Use Them', body: 'We use cookies to keep you signed in, remember your language and theme preferences, understand usage patterns and improve the platform.' },
      { heading: '3. Managing Cookies', body: 'You can control or delete cookies through your browser settings. Disabling cookies may affect some features of the platform.' },
    ],
  },
  'copyright-infringement': {
    tag: 'Legal',
    title: 'Copyright Infringement Policy',
    intro: 'We respect the intellectual property rights of others and expect our users to do the same.',
    sections: [
      { heading: '1. Filing a Notice', body: 'If you believe content on our platform infringes your copyright, please submit a notice with the disputed content\u2019s location and proof of ownership.' },
      { heading: '2. Counter-Notice', body: 'If you believe your content was removed in error, you may file a counter-notice explaining why the material should be restored.' },
      { heading: '3. Repeat Infringers', body: 'Accounts that repeatedly post infringing content may be suspended or terminated at our discretion.' },
    ],
  },
};

export default function Legal() {
  const { t, lang } = useLang();
  const type = window.location.pathname.split('/')[1] || 'terms';
  const doc = CONTENT[type] || CONTENT.terms;

  return (
    <div style={{ paddingTop: 0, maxWidth: '100vw', overflowX: 'hidden' }}>
      <section style={{ background: 'var(--bg-subtle)', position: 'relative', overflow: 'hidden', minHeight: 320 }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.55) 25%, rgba(255,255,255,0) 50%)' }} />
        <div style={{ position: 'relative', maxWidth: 1200, margin: '0 auto', padding: '56px 32px', textAlign: 'left' }} className="legal-hero-inner">
          <Reveal>
            <div style={{ display: 'inline-block', background: 'rgba(255,122,0,0.12)', color: 'var(--primary)', padding: '6px 18px', borderRadius: 999, fontSize: 17, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
              {doc.tag}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 style={{ fontSize: 'clamp(24px, 3.5vw, 34px)', fontWeight: 900, lineHeight: 1.15, marginBottom: 12, color: 'var(--secondary)', maxWidth: 620 }}>
              {doc.title}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p style={{ color: 'var(--text-muted)', fontSize: 16, lineHeight: 1.7, maxWidth: 560 }}>
              {doc.intro}
            </p>
          </Reveal>
        </div>
      </section>

      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '0 32px 80px' }} className="legal-body">
        {/* Policy type switcher — horizontal bar above the document */}
        <nav className="legal-nav" aria-label="Policy types">
          {Object.keys(CONTENT).map(key => (
            <Link key={key} to={`/${key}`} className={key === type ? 'legal-nav-link is-active' : 'legal-nav-link'}>
              {CONTENT[key].title}
            </Link>
          ))}
        </nav>

        <div className="legal-content">
            {doc.sections.map((s, i) => (
              <Reveal key={s.heading} delay={i * 0.05}>
                <div style={{ background: 'var(--bg-main)', borderRadius: 20, padding: '24px 28px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 2px 12px rgba(0,0,0,0.03)' }} className="legal-card">
                  <h2 style={{ fontSize: 18, fontWeight: 900, color: 'var(--secondary)', marginBottom: 8 }}>{s.heading}</h2>
                  <p style={{ color: 'var(--text-muted)', fontSize: 16, lineHeight: 1.7, margin: 0 }}>{s.body}</p>
                </div>
              </Reveal>
            ))}
            <Reveal>
              <p style={{ color: 'var(--text-light)', fontSize: 15, textAlign: 'center', paddingTop: 12 }}>
                {t.lastUpdated}: {new Date().toLocaleDateString(localeFor(lang), { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </Reveal>
        </div>
      </section>

      <style>{`
        :root.dark .privacy-hero-overlay, [data-theme="dark"] .privacy-hero-overlay {
          background: linear-gradient(90deg, rgba(15,23,42,0.9) 0%, rgba(15,23,42,0.55) 30%, rgba(15,23,42,0) 55%);
        }

        /* Horizontal policy switcher */
        .legal-nav {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          padding: 22px 0 26px;
          margin-bottom: 28px;
          border-bottom: 1px solid rgba(127,127,127,0.22);
        }
        .legal-nav-link {
          padding: 11px 20px;
          border-radius: 999px;
          font-size: 15px;
          font-weight: 700;
          text-decoration: none;
          white-space: nowrap;
          background: var(--bg-main);
          color: var(--text-muted);
          border: 1px solid rgba(127,127,127,0.2);
          box-shadow: 0 2px 8px rgba(0,0,0,0.03);
          transition: background 0.2s, color 0.2s, box-shadow 0.2s, transform 0.2s;
        }
        .legal-nav-link:hover {
          color: var(--primary);
          border-color: var(--primary);
          transform: translateY(-2px);
        }
        .legal-nav-link.is-active {
          background: var(--primary);
          color: #fff;
          border-color: var(--primary);
          box-shadow: 0 4px 16px rgba(255,122,0,0.25);
        }

        @media (max-width: 768px) {
          .legal-hero-inner { padding: 40px 20px !important; }
          .legal-body { padding: 0 16px 60px !important; }
          .legal-nav {
            flex-wrap: nowrap;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            padding-bottom: 20px;
          }
          .legal-nav::-webkit-scrollbar { display: none; }
          .legal-nav-link { flex: 0 0 auto; padding: 9px 16px !important; font-size: 14px !important; }
          .legal-content { gap: 16px !important; }
          .legal-card { padding: 18px 16px !important; }
        }
      `}</style>
    </div>
  );
}